import { instrumentRunQueryKeys, studyQueryKeys } from '@/config/queryKeys';
import { STUDIES_QUERY_GC_TIME, STUDIES_QUERY_STALE_TIME } from '@/constants';
import InstrumentRunConsumables from '@/pages/Instrument_Runs/components/InstrumentRunConsumables';
import InstrumentRunHeader from '@/pages/Instrument_Runs/components/InstrumentRunHeader/InstrumentRunHeader';
import { RunActionRow } from '@/pages/Instrument_Runs/components/RunActionRow';
import RunControlsAndSamples from '@/pages/Instrument_Runs/components/RunControlsAndSamples';
import { RunEditForm, type RunEditFormHandle } from '@/pages/Instrument_Runs/components/RunEditForm';
import type { InstrumentRunForm } from '@/pages/Instrument_Runs/types';
import { fetchOrganizations } from '@/pages/Organizations/orgUtils';
import { fetchInstrumentRun } from '@/services/instrumentRuns';
import { searchStudies } from '@/services/studies';
import { useAppStore } from '@/stores/app';
import type { InstrumentRunPublic, Org, PaginatedStudiesResponse } from '@/types';
import { FullLoading } from '@components/index';
import { Stack } from '@nautilusbio/nb-design-system/Stack';
import { Text } from '@nautilusbio/nb-design-system/Text';
import { useQuery, useSuspenseQuery } from '@tanstack/react-query';
import { ErrorComponent, createFileRoute, redirect, useNavigate } from '@tanstack/react-router';
import React, { createElement, useCallback, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';
import { useShallow } from 'zustand/react/shallow';

import {
  INSTRUMENT_RUN_COMPLETED_STALE_TIME,
  INSTRUMENT_RUN_SUBMITTED_STALE_TIME,
  INSTRUMENT_RUN_GC_TIME,
} from '../../constants';
import irStyles from '../../pages/Instrument_Runs/styles.module.css';

const instrumentRunSearchSchema = z.object({
  edit: z.coerce.boolean().optional(),
});

export const Route = createFileRoute('/instrument_runs/$instrumentRunId')({
  beforeLoad: ({ context }) => {
    if (context.authentication.status === 'UNAUTHENTICATED') {
      throw redirect({ to: '/login' });
    }
  },
  loaderDeps: ({ search }) => ({ edit: search.edit }),
  loader: async ({ context, params, deps }) => {
    const store = useAppStore.getState();
    const selectedOrg = store.selectedOrganization ?? store.selectedOrgStub;

    if (!selectedOrg?.id) {
      throw redirect({ to: '/organizations' });
    }

    const instrumentRun = await context.queryClient.fetchQuery<InstrumentRunPublic>({
      queryKey: instrumentRunQueryKeys.detail(selectedOrg.id, params.instrumentRunId),
      queryFn: () => fetchInstrumentRun(selectedOrg.id, params.instrumentRunId),
      staleTime: 0,
      gcTime: INSTRUMENT_RUN_GC_TIME,
    });

    if (instrumentRun?.status === 'SUBMITTED') {
      context.queryClient.setQueryDefaults(instrumentRunQueryKeys.detail(selectedOrg.id, params.instrumentRunId), {
        staleTime: INSTRUMENT_RUN_SUBMITTED_STALE_TIME,
        gcTime: INSTRUMENT_RUN_GC_TIME,
      });
    } else if (instrumentRun?.status === 'COMPLETED') {
      context.queryClient.setQueryDefaults(instrumentRunQueryKeys.detail(selectedOrg.id, params.instrumentRunId), {
        staleTime: INSTRUMENT_RUN_COMPLETED_STALE_TIME,
        gcTime: INSTRUMENT_RUN_GC_TIME,
      });
    }

    // When entering edit mode or for draft runs, prefetch studies and fetch organizations
    let organizations: Org[] = [];
    if (deps.edit || instrumentRun?.status === 'DRAFT') {
      try {
        organizations = await fetchOrganizations();
      } catch {
        // proceed with empty list
      }

      try {
        await context.queryClient.prefetchQuery<PaginatedStudiesResponse>({
          queryKey: studyQueryKeys.list(selectedOrg.id),
          queryFn: async () =>
            searchStudies(selectedOrg.id, {
              skip: 0,
              limit: 500,
              sort_by: 'created_at',
              sort_order: 'desc',
            }),
          staleTime: STUDIES_QUERY_STALE_TIME,
          gcTime: STUDIES_QUERY_GC_TIME,
        });
      } catch {
        // prefetch failed — component will fetch on mount
      }
    }

    return { instrumentRun, organizations, orgId: selectedOrg.id };
  },
  component: RouteComponent,
  errorComponent: e => <ErrorComponent {...e} />,
  pendingComponent: PendingComponent,
  validateSearch: instrumentRunSearchSchema,
});

function PendingComponent() {
  return createElement(FullLoading, { addEllipsis: true, label: 'Loading run' });
}

function RouteComponent() {
  const { t } = useTranslation('runs');
  const navigate = useNavigate();
  const selectedOrg = useAppStore(useShallow(state => state.selectedOrganization ?? state.selectedOrgStub));
  const { instrumentRunId } = Route.useParams();
  const { edit } = Route.useSearch();
  const { instrumentRun: _loaderRun, organizations, orgId } = Route.useLoaderData();
  const selectedOrgId = orgId ?? selectedOrg?.id ?? '';

  const { data } = useSuspenseQuery<InstrumentRunPublic>({
    queryKey: instrumentRunQueryKeys.detail(selectedOrgId, instrumentRunId),
    queryFn: () => fetchInstrumentRun(selectedOrgId, instrumentRunId),
    staleTime: INSTRUMENT_RUN_SUBMITTED_STALE_TIME,
    gcTime: INSTRUMENT_RUN_GC_TIME,
  });

  const isEditing = !!edit;
  const [editForm, setEditForm] = useState<InstrumentRunForm | undefined>(undefined);
  const formRef = useRef<RunEditFormHandle>(null);

  // Callback ref: captures the form instance when RunEditForm mounts
  const handleFormRef = useCallback((handle: RunEditFormHandle | null) => {
    (formRef as React.MutableRefObject<RunEditFormHandle | null>).current = handle;
    setEditForm(handle?.form);
  }, []);

  const handleCancel = useCallback(() => {
    void navigate({ to: '/instrument_runs/$instrumentRunId', params: { instrumentRunId }, search: {} });
    setEditForm(undefined);
  }, [navigate, instrumentRunId]);

  const handleSave = useCallback(() => {
    void navigate({ to: '/instrument_runs/$instrumentRunId', params: { instrumentRunId }, search: {} });
    setEditForm(undefined);
  }, [navigate, instrumentRunId]);

  const { data: studiesData } = useQuery<PaginatedStudiesResponse>({
    queryKey: studyQueryKeys.list(selectedOrgId),
    queryFn: async () =>
      searchStudies(selectedOrgId, {
        skip: 0,
        limit: 500,
        sort_by: 'created_at',
        sort_order: 'desc',
      }),
    enabled: isEditing,
    staleTime: STUDIES_QUERY_STALE_TIME,
    gcTime: STUDIES_QUERY_GC_TIME,
  });

  const studies = useMemo(() => {
    const d = studiesData as PaginatedStudiesResponse | undefined;
    if (!d || !Array.isArray(d.records)) return [];
    return d.records;
  }, [studiesData]);

  if (!selectedOrg?.id) {
    return <ErrorComponent error={new Error('No organization selected')} />;
  }

  if (!data) {
    return <Text>{t('empty', { ns: 'general' })}</Text>;
  }

  return (
    <Stack className={irStyles.contentContainer} fullHeight fullWidth gap={3} orientation="vertical">
      <RunActionRow
        isEditing={isEditing}
        onCancel={handleCancel}
        onEdit={() =>
          void navigate({
            to: '/instrument_runs/$instrumentRunId',
            params: { instrumentRunId },
            search: { edit: true },
          })
        }
        onSave={() => formRef.current?.submit()}
        onSubmit={data.status === 'DRAFT' ? () => formRef.current?.submitDraft() : undefined}
        run={data}
      />

      <InstrumentRunHeader form={editForm} isEditing={isEditing} run={data} />

      {isEditing ? (
        <RunEditForm
          ref={handleFormRef}
          mode="edit"
          orgId={selectedOrgId}
          organizations={organizations}
          run={data}
          studies={studies}
          onCancel={handleCancel}
          onSave={handleSave}
        />
      ) : (
        <>
          {data.samples?.length > 0 && <RunControlsAndSamples id={data.id} samples={data.samples} />}
          <InstrumentRunConsumables isEditing={false} values={data.consumables} />
        </>
      )}
    </Stack>
  );
}
